import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { api } from '@/shared/api/http'
import {
  PAGE_DEFAULT_LIMIT,
  type Page,
  type ServiceInvoiceOrderBy,
  type ServiceInvoiceOut,
} from '@/shared/api/types'
import { isMoney, toCents } from '@/shared/money/money'

/** pending → issued; canceled é terminal. */
export type ServiceInvoiceStatus = 'pending' | 'issued' | 'canceled'

export const serviceInvoiceKeys = {
  all: () => ['service-invoices'] as const,
  lists: () => [...serviceInvoiceKeys.all(), 'list'] as const,
  list: (p: ServiceInvoiceListParams) => [...serviceInvoiceKeys.lists(), p] as const,
} as const

export interface ServiceInvoiceListParams {
  readonly status?: ServiceInvoiceStatus
  readonly stakeholder_id?: string
  readonly schedule_id?: string
  /** Substring, sem diferenciar caixa, na descrição OU no nome do tomador. */
  readonly search?: string
  readonly order_by?: ServiceInvoiceOrderBy
  readonly limit?: number
  readonly offset?: number
}

export const serviceInvoicesQueryOptions = (p: ServiceInvoiceListParams = {}) =>
  queryOptions({
    queryKey: serviceInvoiceKeys.list(p),
    queryFn: ({ signal }) =>
      api.get<Page<ServiceInvoiceOut>>(
        '/v1/service-invoices',
        { ...p, limit: p.limit ?? PAGE_DEFAULT_LIMIT },
        signal,
      ),
    placeholderData: keepPreviousData,
  })

const moneyField = z.string().refine((v) => isMoney(v) && toCents(v) > 0, {
  message: 'validation:amountPositive',
})

export const serviceInvoiceFormSchema = z.object({
  stakeholderId: z.uuid('validation:required'),
  description: z.string().trim().min(1, 'validation:required').max(500, 'validation:maxLength'),
  amount: moneyField,
  /** '' = sem vínculo com agendamento. */
  scheduleId: z.string(),
})

export type ServiceInvoiceFormValues = z.infer<typeof serviceInvoiceFormSchema>

export interface ServiceInvoiceCreateBody {
  readonly stakeholder_id: string
  readonly description: string
  readonly amount: string
  readonly schedule_id?: string
}

export function toServiceInvoiceCreateBody(v: ServiceInvoiceFormValues): ServiceInvoiceCreateBody {
  return {
    stakeholder_id: v.stakeholderId,
    description: v.description,
    amount: v.amount,
    ...(v.scheduleId !== '' && { schedule_id: v.scheduleId }),
  }
}

/**
 * `POST /v1/service-invoices` — registra a nota de serviço.
 *
 * ⚠️ ESCOPO: REGISTRA a nota; a EMISSÃO na prefeitura é adapter futuro (por
 * isso a tela é `partial` no nav-config). O status nasce `pending` e só vira
 * `issued` quando essa integração existir.
 *
 * Efeito financeiro → exige Idempotency-Key (chave de INTENÇÃO, de fora).
 */
export function createServiceInvoice(
  body: ServiceInvoiceCreateBody,
  idempotencyKey: string,
): Promise<{ data: ServiceInvoiceOut; created: boolean }> {
  return api.post<ServiceInvoiceOut>('/v1/service-invoices', {
    body,
    idempotencyKey,
    operationPath: '/v1/service-invoices',
  })
}

/** `POST /v1/service-invoices/{id}/cancel` — terminal; repetir → 409. */
export function cancelServiceInvoice(id: string): Promise<ServiceInvoiceOut> {
  return api.post<ServiceInvoiceOut>(`/v1/service-invoices/${id}/cancel`, {}).then((r) => r.data)
}
